var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useCallback, useRef, useState, useEffect } from 'react';
import { cx, css as emotionCss } from 'emotion';
import { inputChange, useCitrusForm } from '@citrus/form';
import { Input, CharacterCount, styles, validationAccessibility } from '@citrus/input';
import { getAutomation, getDebug, getStyleNames, useCitrusDebug, useDebounce, useFormInputId, useUpdateValidation, useUserManagedValidation, useValidationOnMount, } from '@citrus/runtime';
import { useTheme } from '@citrus/user';
import { isNotNilOrEmpty, isNilOrEmpty, only, isString } from '@pcty/utils';
import { Textarea as XTextarea } from './Textarea.styled';
import { disabled as disabledStylesCitron, error as errorStylesCitron } from '../styles/citron';
import { getPerformanceTimeout } from './private/helpers';
var disabledStyles = styles.disabled, errorStyles = styles.error;
var VERSION = '5.6.0';
var Textarea = React.forwardRef(function (_a, ref) {
    var _b;
    var _c, _d;
    var _e = _a.autoFocus, autoFocus = _e === void 0 ? false : _e, automationId = _a.automationId, _f = _a.characterCountOverflow, characterCountOverflow = _f === void 0 ? false : _f, className = _a.className, _g = _a.classNameWrapper, classNameWrapper = _g === void 0 ? '' : _g, css = _a.css, disabled = _a.disabled, _h = _a.emptyLabel, emptyLabel = _h === void 0 ? false : _h, errorMessage = _a.errorMessage, footnote = _a.footnote, _j = _a.hideOptionalLabel, hideOptionalLabel = _j === void 0 ? false : _j, id = _a.id, label = _a.label, _k = _a.labelAddOn, labelAddOn = _k === void 0 ? function () { return null; } : _k, labelPosition = _a.labelPosition, _l = _a.maxLength, maxLength = _l === void 0 ? -1 : _l, onBlur = _a.onBlur, onChange = _a.onChange, onFocus = _a.onFocus, _m = _a.performance, performance = _m === void 0 ? 'debounce' : _m, performanceTimeout = _a.performanceTimeout, _o = _a.placeholder, placeholder = _o === void 0 ? '' : _o, _p = _a.readOnly, readOnly = _p === void 0 ? false : _p, _q = _a.required, required = _q === void 0 ? false : _q, _r = _a.rows, rows = _r === void 0 ? 5 : _r, _s = _a.showCharacterCount, showCharacterCount = _s === void 0 ? false : _s, _t = _a.showRequiredLabel, showRequiredLabel = _t === void 0 ? false : _t, showValidationState = _a.showValidationState, tooltip = _a.tooltip, valid = _a.valid, validate = _a.validate, _u = _a.value, value = _u === void 0 ? '' : _u, _v = _a.__TYPE, __TYPE = _v === void 0 ? 'Textarea' : _v, other = __rest(_a, ["autoFocus", "automationId", "characterCountOverflow", "className", "classNameWrapper", "css", "disabled", "emptyLabel", "errorMessage", "footnote", "hideOptionalLabel", "id", "label", "labelAddOn", "labelPosition", "maxLength", "onBlur", "onChange", "onFocus", "performance", "performanceTimeout", "placeholder", "readOnly", "required", "rows", "showCharacterCount", "showRequiredLabel", "showValidationState", "tooltip", "valid", "validate", "value", "__TYPE"]);
    var debug = useCitrusDebug();
    var isCitron = useTheme().citrus2022;
    var _w = useCitrusForm(), form = _w.form, dispatch = _w.dispatch, customValidate = _w.validate, formDisabled = _w.disabled;
    var isRequired = required === true || isString(required);
    var requiredMessage = isString(required) ? required : undefined;
    var _performanceTimeout = getPerformanceTimeout(performance, performanceTimeout);
    // #region state
    var _x = useState({ valid: false }), _validation = _x[0], setValidation = _x[1];
    var formInputId = useFormInputId(id);
    var userManagedValidation = useUserManagedValidation(valid, errorMessage, showValidationState, setValidation);
    var _y = useState(''), inputValue = _y[0], setInputValue = _y[1];
    var _z = useState(autoFocus), focus = _z[0], setFocus = _z[1];
    var autoCompleteItemSelected = useRef(false);
    var eventType = useRef();
    var lastEmittedValue = useRef(value);
    // #endregion state
    // #region callbacks
    // eslint-disable-next-line react-hooks/exhaustive-deps
    var updateForm = useCallback(inputChange(dispatch, customValidate), [customValidate]);
    var validateRequired = useCallback(function (value) {
        if (isNilOrEmpty(value)) {
            return {
                valid: false,
                errorMessage: requiredMessage !== null && requiredMessage !== void 0 ? requiredMessage : "".concat(label !== null && label !== void 0 ? label : 'This', " is required"),
            };
        }
        return { valid: true };
    }, [label, requiredMessage]);
    var getValidation = useCallback(function (value) {
        if (userManagedValidation && !validate)
            return { valid: valid, errorMessage: errorMessage };
        var validation = { valid: true };
        if (isRequired) {
            validation = validateRequired(value);
        }
        if (validate && validation.valid) {
            validation = validate(value, {
                id: id,
                label: label,
                required: isRequired,
            });
        }
        return validation;
    }, [errorMessage, id, isRequired, label, userManagedValidation, valid, validate, validateRequired]);
    var handleFocus = function () {
        setFocus(true);
        if (onFocus) {
            onFocus({ id: id });
        }
    };
    useEffect(function () { return setInputValue(value); }, [value]);
    useDebounce(function () {
        if (performance !== 'debounce')
            return;
        if (eventType.current === 'change') {
            var validation = getValidation(inputValue);
            lastEmittedValue.current = inputValue;
            setValidation(only(validation, ['valid', 'errorMessage']));
            onChange === null || onChange === void 0 ? void 0 : onChange({
                id: id,
                value: inputValue,
                validation: validation,
            });
            if (!userManagedValidation) {
                updateForm(formInputId, __assign(__assign({}, validation), { value: inputValue, id: id, label: label, dirty: true }));
            }
        }
    }, _performanceTimeout, [inputValue, eventType.current]);
    var handleInputChange = function (e) {
        setInputValue(e.target.value);
        autoCompleteItemSelected.current = false;
        var validation = getValidation(inputValue);
        eventType.current = 'change';
        if (!focus) {
            handleFocus();
        }
        onChange === null || onChange === void 0 ? void 0 : onChange({
            id: id,
            value: e.target.value,
            validation: validation,
        });
    };
    var _onBlur = useCallback(function () {
        if (onBlur)
            onBlur({ id: id });
    }, [id, onBlur]);
    // #endregion callbacks
    // #region effects
    useValidationOnMount(id, label, value, userManagedValidation, getValidation, setValidation, updateForm);
    useUpdateValidation(form, formInputId, valid, errorMessage, _validation, setValidation);
    // if required changes after mount, revalidate
    useEffect(function () {
        var _a, _b, _c;
        var validation = getValidation(value);
        setValidation(validation);
        updateForm(formInputId, __assign(__assign({}, validation), { value: value, id: id, label: label, dirty: (_c = (_b = (_a = form === null || form === void 0 ? void 0 : form.inputs) === null || _a === void 0 ? void 0 : _a[formInputId]) === null || _b === void 0 ? void 0 : _b.dirty) !== null && _c !== void 0 ? _c : false }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [required]);
    // #endregion effects
    // #region styles
    var disabledStylesCss = isCitron ? disabledStylesCitron : disabledStyles;
    var errorStylesCss = isCitron ? errorStylesCitron : errorStyles;
    var emotionObj = (_b = {},
        _b[emotionCss(css)] = isNotNilOrEmpty(css),
        _b[emotionCss(errorStylesCss)] = !_validation.valid && (showValidationState || (form === null || form === void 0 ? void 0 : form.showValidationState)),
        _b[emotionCss(disabledStylesCss)] = disabled !== null && disabled !== void 0 ? disabled : formDisabled,
        _b);
    var styleNames = {
        error: (_c = emotionObj[emotionCss(errorStyles)]) !== null && _c !== void 0 ? _c : false,
        disabled: (_d = emotionObj[emotionCss(disabledStyles)]) !== null && _d !== void 0 ? _d : false,
    };
    var classes = cx(emotionObj, className || '');
    var maxLengthValue = characterCountOverflow ? -1 : maxLength;
    var _showValidationState = showValidationState !== undefined ? showValidationState : form === null || form === void 0 ? void 0 : form.showValidationState;
    return (React.createElement(Input, { automationId: automationId, className: classNameWrapper, disabled: disabled !== null && disabled !== void 0 ? disabled : formDisabled, emptyLabel: emptyLabel, errorMessage: _validation.errorMessage, footnote: footnote, hideOptionalLabel: hideOptionalLabel, id: id, label: label, labelAddOn: labelAddOn, labelPosition: labelPosition, readOnly: readOnly, ref: ref, renderCharacterCount: function () {
            return showCharacterCount ? React.createElement(CharacterCount, { automationId: automationId, max: maxLength, text: value }) : null;
        }, required: isRequired, showRequiredLabel: showRequiredLabel, showValidationState: showValidationState !== undefined ? showValidationState : form === null || form === void 0 ? void 0 : form.showValidationState, tooltip: tooltip, valid: _validation.valid },
        React.createElement(XTextarea, __assign({}, other, getDebug(debug, __TYPE, VERSION), getAutomation(automationId), validationAccessibility(id, _validation.valid, _showValidationState), { className: classes, disabled: disabled !== null && disabled !== void 0 ? disabled : formDisabled, id: id, maxLength: maxLengthValue, onBlur: _onBlur, onChange: handleInputChange, placeholder: placeholder, readOnly: readOnly, required: isRequired, rows: rows, style: styles, styleNames: getStyleNames(styleNames), value: inputValue }))));
});
Textarea.defaultProps = {
    __TYPE: 'Textarea',
};
export default Textarea;
